import { MinusIcon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/web/components/primitives/button";
import { Input } from "@/web/components/primitives/input";
import { formatDollars, parseDollars } from "@/web/lib/formatting";
import { cn } from "@/web/lib/styling";

const INTEREST_STEPS = [10, 100, 500, 1_000, 5_000, 10_000];
const NOMINATION_UPPER_LIMIT = 100_000;

function useInterestSelector(
  interestAmount: number,
  setInterestAmount: (interestAmount: number) => void,
) {
  // Local state for the formatted display value
  const [displayValue, setDisplayValue] = useState(() => formatDollars(interestAmount));

  // Sync display value when interestAmount changes externally (e.g., from buttons)
  useEffect(() => {
    setDisplayValue(formatDollars(interestAmount));
  }, [interestAmount]);

  // Find the first step greater than interestAmount (next step up)
  let nextIndex = -1;
  for (let index = 0; index < INTEREST_STEPS.length; index++) {
    const step = INTEREST_STEPS[index];
    if (step !== undefined && interestAmount < step) {
      nextIndex = index;
      break;
    }
  }

  // Find the last step less than interestAmount (previous step down)
  let previousIndex = -1;
  for (let index = INTEREST_STEPS.length - 1; index >= 0; index--) {
    const step = INTEREST_STEPS[index];
    if (step !== undefined && interestAmount > step) {
      previousIndex = index;
      break;
    }
  }

  function increment() {
    if (nextIndex === -1 || nextIndex >= INTEREST_STEPS.length) return;
    const newAmount = INTEREST_STEPS[nextIndex];
    if (newAmount) setInterestAmount(newAmount);
  }

  function decrement() {
    if (previousIndex === -1 || previousIndex >= INTEREST_STEPS.length) return;
    const newAmount = INTEREST_STEPS[previousIndex];
    if (newAmount) setInterestAmount(newAmount);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const parsedValue = parseDollars(e.target.value) ?? 0;
    const cappedValue = Math.min(parsedValue, NOMINATION_UPPER_LIMIT);
    setDisplayValue(formatDollars(cappedValue));
    setInterestAmount(cappedValue);
  }

  return {
    displayValue,
    handleInputChange,
    increment,
    decrement,
    previousIndex,
    nextIndex,
  };
}

export function DollarSelector({
  interestAmount,
  setInterestAmount,
  inputId,
  inputClassName,
}: {
  interestAmount: number;
  setInterestAmount: (interestAmount: number) => void;
  inputId?: string;
  inputClassName?: string;
}) {
  const { displayValue, handleInputChange, increment, decrement, previousIndex, nextIndex } =
    useInterestSelector(interestAmount, setInterestAmount);

  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <Button
        disabled={previousIndex === -1 || previousIndex >= INTEREST_STEPS.length}
        size="icon"
        className="shrink-0 rounded-full"
        onClick={decrement}
      >
        <MinusIcon />
      </Button>
      <Input
        id={inputId}
        className={cn(
          "withus-strong min-w-0 flex-1 cursor-pointer border-none text-center text-[28px]! md:text-[40px]!",
          inputClassName,
        )}
        value={displayValue}
        onChange={handleInputChange}
        inputMode="numeric"
      />
      <Button
        disabled={nextIndex === -1 || nextIndex >= INTEREST_STEPS.length}
        className="shrink-0 rounded-full"
        size="icon"
        onClick={increment}
      >
        <PlusIcon />
      </Button>
    </div>
  );
}
